import type { ArenaSummary, MissionDifficulty, MissionKind } from '@shared/protocol';
import type { TankSpec } from '@shared/tank-spec';

export type { ArenaSummary };

/**
 * The HTTP half of the client.
 *
 * Since the template port there is no access token in the page at all: both
 * tokens are httpOnly cookies (`gamer_id`, 15 minutes; `refreshToken`, 3 days)
 * set by t-game-core's auth, so nothing that gets script into the page can
 * read a credential. The page cannot see them either, which is why the
 * session hint in `state/session.ts` exists.
 *
 * Every request goes through `request()`, so the 401 → refresh → retry dance
 * happens once, here, instead of at forty call sites.
 */

import type {
  BillingAddressDto,
  BillingAddressView,
  CreateDepositResponse,
  CreateWithdrawalRequest,
  CreateWithdrawalResponse,
  DepositOrder,
  PaymentMethodsResponse,
  Withdrawal,
  WithdrawalLimits,
} from '../core/payments/types';
import { collectBrowserInfo } from '../core/payments/methods';

export type { BillingAddressDto, BillingAddressView, DepositOrder, PaymentMethodsResponse };

const BASE = import.meta.env.VITE_API_URL || '';

export interface PublicPlayer {
  id: string;
  nickname: string;
  email: string | null;
  /**
   * Whether the address has been confirmed.
   *
   * On the session record rather than fetched separately, because it decides
   * *which screen the app is*: `App.vue` renders nothing but the check-your-inbox
   * gate while it is false. Read from the template's own auth response on
   * register, login, refresh and `me`, so it is as fresh as the session is — and
   * `JwtStrategy` re-reads the column from the database on every request, so
   * confirming in one tab unblocks another without a new token.
   */
  emailVerified: boolean;
  /**
   * Whether to offer this account the admin panel.
   *
   * On the session record because `role` is already in the template's auth
   * response on register, login, refresh and `me` — `toPlayer` was simply
   * dropping it. Reading it here costs nothing and, unlike the account view,
   * it is populated the moment the session restores and in a battle as well as
   * the garage, which is where the settings window has to answer the question.
   *
   * **A signal to draw a link with, never an authorisation.** `/admin` is
   * Kottster behind Caddy with its own login and every `/api/admin/*` route is
   * behind `AdminAccessGuard`, so a hand-edited `true` reveals a menu row and
   * then a login the tamperer cannot pass.
   */
  isAdmin: boolean;
  credits: number;
  diamonds: number;
  createdAt: string;
}

/**
 * The template's account row, as t-game-core's auth serves it. The game maps
 * it into the `PublicPlayer` this app was built around.
 */
interface TemplateUser {
  id: string;
  email: string;
  username: string | null;
  role: string;
  emailVerified: boolean;
}

/**
 * Balances are deliberately zero here: the template's auth knows nothing about
 * the game economy, and the garage overview corrects them via `setBalances`
 * moments later. The call sign shown is the username or the email's local
 * part; the authoritative one is the game profile's, served by the garage.
 */
function toPlayer(user: TemplateUser): PublicPlayer {
  return {
    id: user.id,
    nickname: user.username ?? user.email.split('@')[0] ?? 'commander',
    email: user.email,
    emailVerified: user.emailVerified,
    isAdmin: user.role === 'ADMIN',
    credits: 0,
    diamonds: 0,
    createdAt: '',
  };
}

export interface SessionResponse {
  player: PublicPlayer;
}

export type ItemKind = 'HULL' | 'TURRET' | 'PAINT' | 'TITLE';

export interface GarageItem {
  id: string;
  kind: ItemKind;
  name: string;
  blurb: string;
  tier: number;
  /**
   * Credits.
   *
   * On a premium item this is a **valuation the shop never charges** — the
   * credit figure the item is worth, kept on the row because four server-side
   * rules read it. Show `diamondPrice` instead whenever it is set, or the
   * headline number stops being the number that is paid.
   */
  price: number;
  /** Diamonds, when the item is premium. Null for everything on the credit ladder. */
  diamondPrice: number | null;
  owned: boolean;
  equipped: boolean;
  level: number;
  maxUpgradeLevel: number;
  /** Null when unowned, maxed, or while an install is pending. */
  nextUpgradeCost: number | null;
  /** How long the next upgrade takes to install, when one can be started. */
  nextUpgradeSeconds: number | null;
  /** The upgrade being installed; `level` stays the level in use until it is installed. */
  upgrade: UpgradeInstall | null;
  invested: number;
  prestige: number;
  maxPrestigeLevel: number;
  nextPrestigeCost: number | null;
  prestigeUnlocked: boolean;
  prestigeInvested: number;
  requiredResearchId: string | null;
  requiredResearchName: string | null;
  researchSatisfied: boolean;
  /** The rank this may be *bought* at, and that rank's title. 1 means ungated. */
  requiredRank: number;
  requiredRankTitle: string;
  /**
   * Whether the player has reached it.
   *
   * The second, independent gate. A card is locked when either this or
   * `researchSatisfied` is false and it is not already owned — and only when it
   * is not owned, because a gate is permission to buy and never permission to
   * keep. An owned hull whose rank was raised afterwards still fits.
   */
  rankSatisfied: boolean;
  /**
   * No longer sold, but still owned by this player.
   *
   * Only ever arrives on a card the player owns — the server omits a retired
   * item entirely for everybody else — so this is not a "sold out" shelf state.
   * The card prints *No longer sold* where a price would go, because the price
   * on the row is a record of what it cost and not an offer.
   */
  retired: boolean;
  /**
   * The rank this is *given* at, for the honours titles. Null for anything sold.
   *
   * Not a gate: the server refuses to sell these at every rank, including the top
   * one, so the card draws no Buy button at all rather than a disabled one. It
   * arrives on the card whether or not it is owned, because a thing waiting at
   * the top of the ladder is worth showing to somebody climbing.
   */
  awardedAtRank: number | null;
  awardedAtRankTitle: string | null;
  affordable: boolean;
  stats: Record<string, number>;
  currentStats: Record<string, number>;
  /**
   * Stats one upgrade level on, or null when there is no next level to buy.
   *
   * The confirmation dialog's stat difference is this against `currentStats`. It
   * is **served, never computed here**: the upgrade curve lives in
   * `stat-resolution.ts` server-side, and a browser that worked out the next
   * level itself would be a second implementation of it, free to promise a
   * figure the purchase then does not deliver. Same reason prices arrive finished
   * rather than as a formula.
   */
  nextStats: Record<string, number> | null;
  /** The same, one top-tier level on. Null when there is none to buy. */
  nextPrestigeStats: Record<string, number> | null;
  colorPrimary: string | null;
  colorSecondary: string | null;
}

export interface ResearchNode {
  id: string;
  name: string;
  blurb: string;
  cost: number;
  /** Which sub-tree: `mobility`, `protection`, `armament`, `support`. */
  branch: string;
  /**
   * The branch's label and position, carried per node.
   *
   * The client deliberately holds no copy of the branch list: which branches
   * exist, what they are called and what order they come in is catalogue data,
   * and a second copy in the frontend is a second thing to forget to update.
   * The tree draws the name on the branch's root card.
   */
  branchName: string;
  branchOrder: number;
  /** Depth in the tree — a *row*, top to bottom. A prerequisite is always shallower. */
  tier: number;
  /**
   * At most one, and in the same branch — that is what makes the tree a tree
   * and lets it be drawn as brackets that never cross. The field stays an array
   * because the table behind it is many-to-many; a second id still draws, as a
   * connector that crosses.
   */
  prerequisiteIds: string[];
  unlocked: boolean;
  available: boolean;
  affordable: boolean;
  unlocksItemIds: string[];
  /** Hull parts this node puts on the shelf, by name. */
  unlocksPartNames: string[];

  /**
   * The permanent bonus finishing this node grants, worded by the server —
   * `+2% acceleration`, `−3% reload time`, or `null` for a node that grants
   * none.
   *
   * A finished string rather than a stat key and a number, so the client holds
   * no copy of the wording rules or of the sign convention (positive is
   * *better*, so the reload's plus is a minus). Same argument as the price.
   */
  passive: string | null;

  /**
   * Research takes real time, and the wait is skippable for diamonds.
   *
   * All four of these are the server's arithmetic, handed over finished. The
   * duration comes from the node's cost and tier and the skip price from the
   * time *left*, and neither formula exists on this side — the client sends a
   * node id and is told what it costs, exactly as it is for a hull.
   *
   * `secondsRemaining` is a remainder measured at the instant of the response,
   * the same shape as `secondsToNextFuel`: count it down locally, and when it
   * reaches zero **re-read** rather than deciding for yourself that a node is
   * finished. Whether a node is researched is `unlocked`, and only the server
   * says so.
   */
  durationSeconds: number;
  inProgress: boolean;
  /** Whole seconds left at the moment of the response, or null when idle. */
  secondsRemaining: number | null;
  /** Diamonds to finish it now, or null when idle. Falls as the wait does. */
  skipDiamondCost: number | null;
}

/**
 * One rung of the rank ladder, as the server serves it.
 *
 * The client holds **no copy of the rank list** — not the titles, not the
 * thresholds, not what each one unlocks. Same rule the research branches follow:
 * a twenty-first rank is a title in `catalogue.data.ts` and an image in
 * `public/assets/ranks/`, and there must be no second list here to forget.
 *
 * The insignia is the one thing not on the wire, and deliberately: it is derived
 * from `rank` (`/assets/ranks/rank-07.webp`), exactly as a research icon is
 * derived from its node id and a map's picture from its map id. A path is not a
 * fact worth sending.
 */
export interface RankRung {
  rank: number;
  title: string;
  /** Lifetime xp needed to *be* this rank. Cumulative, and 0 at rank 1. */
  xpToReach: number;
  /** What this rank puts on the shelf, by name. Empty above rank 17, on purpose. */
  unlocks: string[];
  /**
   * What this rank **hands over**, by name — as opposed to what it puts on sale.
   *
   * Ranks 18–20 carry these and nothing else does. Kept apart from `unlocks`
   * because the two are different promises and confusing them misleads in the
   * expensive direction: *unlocks* means the shop will now sell it to you,
   * *awards* means it is already yours.
   */
  awards: string[];
  reached: boolean;
}

/**
 * The wallet, as the server resolved it *at the moment of the response*.
 *
 * `fuel` is a derived quantity — the server stores a reading and a timestamp
 * and solves for now — so this snapshot ages. `secondsToNextFuel` and
 * `fuelRegenSeconds` are what let the client run a countdown against it without
 * ever inventing a balance: when the countdown reaches zero the client asks
 * again rather than guessing.
 */
export interface Wallet {
  credits: number;
  diamonds: number;

  fuel: number;
  fuelCapacity: number;
  secondsToNextFuel: number | null;
  secondsToFullFuel: number | null;
  fuelRegenSeconds: number;
  sortieFuelCost: number;
  canDeploy: boolean;
  /** What topping the tank off costs right now. */
  fuelRefillDiamondCost: number;
  /** Fuel tanks owned (1–9); capacity is five a tank. */
  fuelTanks: number;
  /**
   * Credits paid per diamond converted.
   *
   * Read from the wallet rather than held as a constant here, the way the refill
   * price and the rank ladder are. The client must not own a copy of a rate that
   * appears in a confirmation dialog.
   */
  creditsPerDiamond: number;

  xp: number;
  rank: number;
  rankTitle: string;
  xpIntoRank: number;
  xpForNextRank: number | null;

  xpBoostExpiresAt: string | null;
  lootBoostExpiresAt: string | null;
  researchBoostExpiresAt: string | null;
  xpMultiplier: number;
  lootMultiplier: number;
  researchMultiplier: number;
}

export interface DiamondPack {
  id: string;
  name: string;
  blurb: string;
  diamonds: number;
  bonusDiamonds: number;
  total: number;
  /** What the card charges, in minor units of `PremiumShop.pricing.currency`. */
  priceCents: number;
  /**
   * The same price in the catalogue's own currency, so a card converted out of
   * USD can show what it was converted *from* rather than a figure the player
   * cannot check. Equal to `priceCents` when nothing was converted.
   */
  basePriceCents: number;
  currency: string;
  highlight: boolean;
  /** True while no real payment provider is bound — the shop says so. */
  unpaid: boolean;
  /**
   * The live limited offer on this pack, or null — the ordinary case.
   *
   * `priceCents` is already the discounted figure while one runs; this carries
   * what a single price cannot say — the standing price for the struck-through
   * label, and when the sale closes. The countdown is a subtraction against
   * `endsAt` on the shop's own ticking clock; nothing on the wire counts down.
   * The id is posted back with the purchase to pin the quote: the server
   * refuses if the offer closed rather than charging a figure the player never
   * saw.
   */
  offer: { id: string; fullPriceCents: number; endsAt: string } | null;
}

export interface BoostOffer {
  id: string;
  kind: 'XP' | 'LOOT' | 'RESEARCH';
  name: string;
  blurb: string;
  multiplier: number;
  hours: number;
  diamondCost: number;
  active: boolean;
  expiresAt: string | null;
}

/** A refill key: one unit, or the tank topped off. */
export type FuelRefillKey = 1 | 'full';

/**
 * One rung of the instant-fuel ladder — one unit, or the tank topped off.
 * `diamondCost` is the price right now: the fill is priced per missing unit.
 */
export interface FuelRefillTier {
  units: FuelRefillKey;
  diamondCost: number;
  available: boolean;
}

/** Extra fuel tanks: owned (1–9) and what the next one costs (null at the max). */
export interface FuelTanksOffer {
  owned: number;
  max: number;
  unitsPerTank: number;
  nextDiamondCost: number | null;
}

/** An upgrade being installed on an item. Instants are ISO strings. */
export interface UpgradeInstall {
  toLevel: number;
  startedAt: string;
  completesAt: string;
  durationSeconds: number;
  /** As of the overview's read; the client counts down from `completesAt`. */
  secondsRemaining: number;
  ready: boolean;
  /** Diamonds to finish and install now; null once ready. */
  skipDiamondCost: number | null;
}

/**
 * The custom-amount diamond purchase — a rate rather than a price list, so the
 * shop can offer a slider instead of a fixed ladder.
 *
 * Optional on `PremiumShop`: an older server that has not shipped this yet
 * simply omits the field, and the card that would render it is absent rather
 * than broken — the same convention `model: null` on a prop and a missing
 * research passive both follow.
 */
export interface CustomPackConfig {
  /**
   * Money per 100 diamonds, in minor units of the *player's* currency — the rate
   * the slider prices against, already converted, so the slider's own arithmetic
   * needs no exchange rate on the client.
   */
  rateCentsPer100: number;
  /** The same rate in the catalogue's currency, for the derivation line. */
  baseRateCentsPer100?: number;
  minDiamonds: number;
  stepDiamonds: number;
  /** The binding cap, and currency-independent — a diamond count. */
  maxDiamonds: number;
  maxPriceCents: number;
  currency: string;
}

/**
 * Experience bought with diamonds — a rate and its bounds, so the shop can offer
 * a slider rather than a ladder of fixed amounts.
 *
 * Optional for the same reason `CustomPackConfig` is: a server that has not
 * shipped this omits it and the card is absent rather than broken. The rate is
 * served rather than assumed even though it is 1:1 today, because it is expected
 * to move and it appears in a confirmation dialog — a stale copy there is a
 * player believing they were charged wrongly.
 */
export interface XpPurchaseConfig {
  /** XP handed over per diamond spent. */
  xpPerDiamond: number;
  minDiamonds: number;
  stepDiamonds: number;
  maxDiamonds: number;
}

/**
 * Which currency the shop's money figures are in, and how they got there.
 *
 * One block for the response rather than a rate per card: the server resolves the
 * factor once, so every price in one response provably shares one rate.
 * `converted: false` means no rate was available and the figures are the
 * catalogue's own — the card says which currency it is quoting rather than the
 * shop failing to load.
 *
 * Optional, so a client running against a server that predates this still
 * compiles and simply shows no derivation line.
 */
export interface ShopPricing {
  currency: string;
  baseCurrency: string;
  converted: boolean;
  rate: number;
  rateDate: string | null;
}

export interface PremiumShop {
  pricing?: ShopPricing;
  packs: DiamondPack[];
  boosts: BoostOffer[];
  fuelRefill: { capacity: number; tiers: FuelRefillTier[] };
  fuelTanks: FuelTanksOffer;
  customPack?: CustomPackConfig;
  xpPurchase?: XpPurchaseConfig;
  prestige: { maxLevel: number; diamondCosts: number[] };
  paymentsStubbed: boolean;
}

/**
 * A themed hull and turret, sold together for diamonds.
 *
 * It sits beside `shop` rather than inside it because the server builds it in a
 * different place, and for a reason worth keeping visible from this side too:
 * an offer needs a price, the catalogue and the player's shelf, and only the
 * garage holds all three. See `GarageBundleView` on the server.
 *
 * The two ids are what the preview is drawn from — `ItemPreview` with
 * `framing="tank"` turns them into an assembled still through the same
 * `TankVisual` the arena uses, so a bundle card shows the actual tank it makes
 * rather than an artist's mock-up that could drift from it.
 *
 * `ownsHull` / `ownsTurret` are not decoration. A bundle costs the same whether
 * one half is already in the shed or neither is, so the card has to say which —
 * see the purchase path on the server for why that is the right price and why
 * saying so is not optional.
 */
export interface TankBundle {
  id: string;
  name: string;
  blurb: string;
  diamondCost: number;
  hullId: string;
  hullName: string;
  turretId: string;
  turretName: string;
  ownsHull: boolean;
  ownsTurret: boolean;
  /** False once both halves are owned — there is nothing left to sell. */
  available: boolean;
  affordable: boolean;
  /**
   * The live limited offer on this bundle, or null. Same contract as the one
   * on `DiamondPack`: `diamondCost` is already discounted while it runs, the
   * full figure and the deadline are here for the label and the countdown, and
   * the id pins the quote on the purchase. While set, the shop lists the
   * bundle on the limited shelf instead of the regular Bundles one.
   */
  offer: { id: string; fullDiamondCost: number; endsAt: string } | null;
}

/**
 * One daily quest, as the server resolved it.
 *
 * The client holds **no copy of the quest pool** — not the wording, not the
 * targets, not the rewards, not which four are today's. Same rule the research
 * branches and the rank ladder follow: an eleventh quest is an entry in
 * `quests/daily-quests.ts` on the server and nothing at all here.
 *
 * `name` and `blurb` arrive with the featured hull, turret or map already
 * substituted, so the browser never joins an id to a display name.
 */
export interface DailyQuest {
  id: string;
  name: string;
  blurb: string;
  metric: string;
  tier: 'light' | 'standard' | 'hard';
  target: number;
  /** Already clamped to `target`, so a card never reads "34 / 20". */
  progress: number;
  complete: boolean;
  claimed: boolean;
  /** Finished and unpaid. The only state the Claim button is drawn for. */
  claimable: boolean;
  reward: { credits: number; xp: number };
  /** `Team battle`, or null when any mode counts. Worded by the server. */
  modeName: string | null;
  subjectKind: 'hull' | 'turret' | 'map' | null;
  subjectId: string | null;
  subjectName: string | null;
  /**
   * Whether the featured hull or turret is one the player owns. Null when the
   * quest is not about an item.
   *
   * Not a gate — progress is credited from the loadout a sortie was flown with,
   * which can only be something owned. It is here so the window can say *You do
   * not own the Scout* rather than leaving somebody watching a counter that
   * never moves.
   */
  subjectOwned: boolean | null;
}

export interface DailyQuests {
  dayIndex: number;
  /**
   * Whole seconds until the set rolls over, measured at the instant of the
   * response — the same shape as `secondsToNextFuel`. Count it down locally and
   * **re-read** when it reaches zero; whether the day has changed is the
   * server's answer, not an arithmetic the client should be doing.
   */
  secondsUntilReset: number;
  quests: DailyQuest[];
  /** Finished but unpaid. The number on the garage box. */
  unclaimed: number;
  dayReward: { credits: number; xp: number };
  /** Diamonds a re-roll costs — the server's figure, never restated here. */
  rerollCost: number;
  /** False when every quest is finished or paid out. */
  rerollable: boolean;
  /** Re-rolls bought today. */
  rerolls: number;
}

/**
 * What the last finished sortie paid.
 *
 * Still served, no longer shown: the client used to raise a "Battle report"
 * popup off this on the way back to the garage, and that window read as a
 * second reward on top of the result screen that now shows the payout in the
 * arena itself — so the popup is gone and nothing in the client reads this
 * today. Kept on the wire because it is the server's own record of the payout,
 * cheap to carry, and the natural hook for any future service-record screen.
 */
export interface LastSortie {
  sessionId: string;
  credits: number;
  xp: number;
  kills: number;
  deaths: number;
  endedAt: string;
}

/** One candidate fitting for a slot, as the customise window renders it. */
export interface GaragePart {
  id: string;
  slot: string;
  name: string;
  blurb: string;
  tier: number;
  /**
   * Credits — or a valuation nobody is charged, when `diamondPrice` is set.
   *
   * **Scaled for the hull this window was opened for.** The catalogue figure is
   * the starter's; a dearer chassis pays progressively more for the same part, and
   * the server derives both this and the debit from the hull's own price, so the
   * card cannot quote what the purchase will not charge. There is nothing for the
   * client to compute.
   */
  price: number;
  /** Set on the two premium options in each slot; null on the two credit ones. Also per hull. */
  diamondPrice: number | null;
  /**
   * Which end of the slot's shelf this sits at.
   *
   * The diamond option in each grade is the best of that grade — more of what the
   * slot gives and half of what it takes — bounded by `PREMIUM_EDGE`. So the shelf
   * reads budget-credits, budget-diamonds, expensive-credits, expensive-diamonds,
   * with the last being the best part in the slot.
   */
  grade: 'budget' | 'expensive' | null;
  stock: boolean;
  image: string | null;
  owned: boolean;
  fitted: boolean;
  /** Against whichever balance this part actually charges. */
  affordable: boolean;
  locked: boolean;
  lockedReason: string | null;
  /** The research node that gates this part, if any — so a locked card can link to it. */
  requiredResearchId: string | null;
  requiredResearchName: string | null;
  /** Before-and-after for the stats this part moves. Computed server-side. */
  delta: { key: string; label: string; from: number; to: number }[];
}

export interface GarageCustomise {
  hullId: string | null;
  hullName: string | null;
  /** True when this is the hull the player is currently driving. */
  equipped: boolean;
  slots: {
    id: string;
    name: string;
    blurb: string;
    fittedPartId: string;
    /** True when the slot is on its stock fitting, which is not one of the cards. */
    stockFitted: boolean;
    parts: GaragePart[];
  }[];
}

/**
 * One mission beaten, at one difficulty. See `GarageOverview.missions`.
 *
 * `kind` and `difficulty` are plain strings rather than `MissionKind` and
 * `MissionDifficulty`, deliberately: a row naming a mission this build does not
 * know about is data to carry past rather than choke on, exactly as a dangling
 * item id is. The picker only badges the cards it is drawing anyway.
 */
export interface MissionRun {
  kind: string;
  difficulty: string;
  /** ISO 8601. The most recent completion of this pair. */
  completedAt: string;
}

export interface GarageOverview {
  player: {
    id: string;
    nickname: string;
    credits: number;
    equippedHullId: string | null;
    equippedTurretId: string | null;
    equippedPaintId: string | null;
    /**
     * The title worn on this player's name, or null for none.
     *
     * Null is the ordinary state rather than a missing one — a title is bought,
     * and nothing grants a default. The Titles tab draws its "wear nothing"
     * control from this.
     */
    equippedTitleId: string | null;
  };
  wallet: Wallet;
  shop: PremiumShop;
  /** Hull-and-turret pairings sold for diamonds. See `TankBundle`. */
  bundles: TankBundle[];
  items: GarageItem[];
  research: ResearchNode[];
  /** All twenty ranks, in order. See `RankRung`. */
  ranks: RankRung[];
  spec: TankSpec;
  /** The eight part slots of the hull currently equipped. */
  customise: GarageCustomise | null;
  paint: { primary: string; secondary: string };
  /** Today's four, their progress and what each pays. See `DailyQuests`. */
  quests: DailyQuests;
  /**
   * Which missions this account has completed, and on which setting.
   *
   * One row per `(kind, difficulty)` pair actually beaten, so an empty array is
   * "has beaten none" and there is no third state to render. Served rather than
   * inferred: a mission leaves no other trace on an account, so there is nothing
   * adjacent the client could work it out from.
   */
  missions: MissionRun[];
  /** The last finished sortie's payout, or null if there has never been one. */
  lastSortie: LastSortie | null;
  stats: {
    sessionsJoined: number;
    distanceDriven: number;
    secondsInArena: number;
    creditsEarned: number;
    xp: number;
  } | null;
}

/** Carries the server's message so the UI can show what actually went wrong. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * ─── The profile screen's shapes ──────────────────────────────────────────────
 *
 * Four of them are the **template's** responses, typed here and called directly
 * rather than proxied through a game endpoint — `/api/me/preferences`,
 * `/api/i18n/config`, `/api/invoices` and `/api/cms/legal/:slug` all already
 * serve exactly what the screen needs, and a game-side wrapper would be a second
 * reader of a table this repo does not own. Only what the template has no answer
 * for lives under `/api/account`.
 */

/**
 * The billing address, as the Account pane reads and writes it.
 *
 * Every field is nullable because a half-filled address is a real state. The
 * server folds `''` to `null`, so a form can post empty strings without inventing
 * a second spelling of "not set".
 *
 * `country` is an ISO 3166-1 alpha-2 code from `@shared/countries` — the code is
 * stored, the name is only ever shown.
 */
export interface BillingAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  postcode: string | null;
  country: string | null;
}

/** `GET`/`PATCH /api/account` — the Account pane. */
export interface AccountProfile {
  id: string;
  /** Non-editable. The screen says why. */
  email: string;
  emailVerified: boolean;
  /** The name on an invoice. Null is ordinary, not an error. */
  fullName: string | null;
  /**
   * The arena call sign. Editable since the rename landed — `updateCallSign`
   * below. Still the garage's column; this surface can now write it.
   */
  callSign: string | null;
  /** Null means never chosen; the server falls back to its own default. */
  locale: string | null;
  currency: string | null;
  /**
   * The address in force now, or null if one was never saved.
   *
   * The server stores these as an append-only history, so what an *invoice*
   * prints is the version that was current when it was issued — not this. A
   * player editing their address does not change an invoice they already have.
   */
  billingAddress: BillingAddress | null;
  createdAt: string;
}

/** `GET`/`PATCH /api/me/preferences` — the template's. */
export interface AccountPreferences {
  locale: string | null;
  currency: string | null;
}

/**
 * `GET /api/i18n/config` — the template's, and the *only* source of the two
 * dropdowns' options.
 *
 * Fetched rather than hard-coded for the reason the research branches and the
 * rank ladder are: a locale enabled in `LOCALES` should appear in the picker by
 * existing, and a currency dropped from `CURRENCIES` should vanish from it, with
 * no second list here to go stale.
 */
export interface I18nConfig {
  locales: { code: string; tag: string; label: string }[];
  defaultLocale: string;
  currencies: { code: string; symbol: string }[];
  defaultCurrency: string;
}

/** One row of the Billing pane — `GET /api/invoices`, the template's. */
export interface InvoiceRow {
  id: string;
  number: string;
  type: string;
  amountCents: number;
  vatCents: number;
  totalCents: number;
  currency: string;
  description: string;
  status: string;
  createdAt: string;
}

/** One row of the Purchases pane — `GET /api/account/purchases`. */
export interface PurchaseRow {
  id: string;
  kind: 'diamonds' | 'credits' | 'other';
  currencyType: string;
  /** Signed: negative for a spend, so the pane reads as a statement. */
  amount: number;
  balanceAfter: number;
  description: string;
  reference: string | null;
  /** Present when the movement was billed — drives the PDF icon. */
  invoiceNumber: string | null;
  at: string;
}

// ─── Commission Office ───────────────────────────────────────────────────────

export type MarketKind = 'HULL' | 'TURRET' | 'TITLE' | 'PAINT' | 'PART';

/** Something the player owns that could go on sale, and whether it may. */
export interface MarketSellable {
  kind: MarketKind;
  itemId: string;
  /** The hull a part belongs to. */
  hullId: string | null;
  name: string;
  level: number;
  prestige: number;
  paid: number;
  /** The ceiling on the asking price. The slider stops here. */
  maxPrice: number;
  listable: boolean;
  /** Why not, when `listable` is false. */
  reason: string | null;
}

export interface MarketListing {
  id: string;
  kind: MarketKind;
  itemId: string;
  hullId: string | null;
  name: string;
  level: number;
  prestige: number;
  price: number;
  durationHours: number;
  /** ISO instants. */
  listedAt: string;
  endsAt: string;
  status: 'ACTIVE' | 'SOLD' | 'CANCELLED' | 'EXPIRED';
  soldAt: string | null;
  soldPrice: number | null;
  /** The office's fee on the viewer's own sale (`@shared/market-fee`); null on anything else. */
  saleFee: number | null;
  /** The viewer's own. */
  mine: boolean;
}

export interface MarketBrowse {
  listings: MarketListing[];
  page: number;
  pageSize: number;
  total: number;
}

export interface MarketMine {
  active: MarketListing[];
  sold: MarketListing[];
}

export type MarketSort = 'newest' | 'price_asc' | 'price_desc' | 'ending';

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

/** One of the three documents — `GET /api/cms/legal/:slug`, the template's. */
export interface LegalDocument {
  slug: string;
  title: string;
  content: string;
  updatedAt: string | null;
}

/** One line in the lobby room. The wire carries no author-kind field, by design. */
export interface ChatMessage {
  id: string;
  author: string;
  body: string;
  /** Epoch milliseconds, from the server's clock. */
  at: number;
}

export interface CheckoutResult {
  url: string;
  completed: boolean;
  diamonds: number;
  sessionId: string;
}

/** Base (EUR) rate of the custom slider — mirrors `CUSTOM_PACK.rateCentsPer100`. */
export const CUSTOM_BASE_RATE_CENTS_PER_100 = 200;

/** Where the gateway sends the player back (`&order=<id>` is appended by core). */
export const DEPOSIT_RETURN_PATH = '/?topup=return';

async function createDeposit(body: { method: string; currency: string; productId?: string; amountCents?: number }): Promise<CheckoutResult> {
  const res = await request<CreateDepositResponse>('/api/payments/deposits', {
    method: 'POST',
    body: JSON.stringify({ ...body, browser: collectBrowserInfo(), returnPath: DEPOSIT_RETURN_PATH }),
  });
  return { url: res.redirectUrl, completed: false, diamonds: 0, sessionId: res.orderId };
}

export interface TopupSessionStatus {
  status: 'pending' | 'completed' | 'expired';
  diamonds: number;
}

/** Deduplicates concurrent refreshes — several 401s must not race each other. */
let refreshing: Promise<boolean> | null = null;

/**
 * Exported for `admin/admin-api.ts`, which is a second, large API surface that
 * wants the same cookie, the same 401-refresh interceptor and the same
 * `ApiError` — but has no business being a hundred more methods on `api`
 * below, since nothing outside the admin panel ever calls one of them.
 */
export async function request<T>(
  path: string,
  init: RequestInit = {},
  retryOn401 = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    // Needed for the refresh cookie to be sent and set.
    credentials: 'include',
  });

  if (response.status === 401 && retryOn401 && path !== '/api/auth/refresh') {
    if (await refreshSession()) return request<T>(path, init, false);
  }

  if (!response.ok) throw new ApiError(await readError(response), response.status);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const message = (body as { message?: unknown }).message;
    // class-validator returns an array of messages; the first is enough.
    if (Array.isArray(message)) return String(message[0]);
    if (typeof message === 'string') return message;
  } catch {
    // Fall through to the generic message below.
  }
  return response.status >= 500 ? 'The server had a problem.' : 'That request was refused.';
}

/** True if the session cookies were rotated successfully. */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;

  refreshing = (async () => {
    try {
      const { user } = await request<{ user: TemplateUser }>(
        '/api/auth/refresh',
        { method: 'POST' },
        false,
      );
      onRefreshed?.({ player: toPlayer(user) });
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

/**
 * Notified whenever a refresh succeeds, so the session store can keep the
 * player record current without this module importing Vue.
 */
let onRefreshed: ((session: SessionResponse) => void) | null = null;
export function onSessionRefreshed(handler: (session: SessionResponse) => void): void {
  onRefreshed = handler;
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const api = {
  /**
   * `altcha` is the solved proof-of-work payload from the ALTCHA widget — the
   * template requires one on register and login. The call sign travels as the
   * template's `username`; the game profile derives its own from it at
   * provisioning.
   */
  async register(body: { username: string; email: string; password: string; altcha: string }) {
    const { user } = await request<{ user: TemplateUser }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { player: toPlayer(user) } satisfies SessionResponse;
  },

  async login(body: { email: string; password: string; altcha: string }) {
    const { user } = await request<{ user: TemplateUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { player: toPlayer(user) } satisfies SessionResponse;
  },

  logout() {
    return request<void>('/api/auth/logout', { method: 'POST' });
  },

  async me() {
    const { user } = await request<{ user: TemplateUser }>('/api/auth/me');
    return { player: toPlayer(user) };
  },

  /**
   * Which arenas are running.
   *
   * Read from the server's in-memory registry, so this is cheap enough for the
   * Arena tab to poll while it is open — there is no query behind it. The
   * numbers age the moment they arrive, which is why the tab re-reads rather
   * than counting joins and leaves itself.
   */
  arenas() {
    return request<{ arenas: ArenaSummary[] }>('/api/battle/arenas');
  },

  /**
   * The maps a room can be opened on.
   *
   * Fetched rather than hard-coded, for the same reason the research tree reads
   * its branches off the nodes: a map added to `shared/maps.ts` should appear in
   * the picker by existing, not by somebody remembering a second list.
   */
  battleMaps() {
    return request<{ maps: { id: string; name: string }[] }>('/api/battle/maps');
  },

  /**
   * Starts an arena and returns it.
   *
   * The client sends a name and a map id and gets an arena id back; it never
   * chooses that id. Same rule as everywhere else — the server owns the keys,
   * the client owns what it meant. An omitted map is the server's default, so
   * the picker is a convenience rather than a required field.
   */
  createArena(name: string, mapId?: string) {
    return request<{ arena: ArenaSummary }>('/api/battle/arenas', {
      method: 'POST',
      body: JSON.stringify({ name, ...(mapId ? { mapId } : {}) }),
    });
  },

  /**
   * Opens a room for one mission and returns it.
   *
   * Sends a **kind** and nothing else that decides the battlefield: the server
   * derives the mode and the map from it together, so there is no way for this
   * call to ask for a mission's rules on the wrong ground. Deliberately not
   * `createArena` with a mode — that route validates against `MATCH_MODES`,
   * which the mission modes are permanently absent from, and would answer with
   * a deathmatch.
   */
  /**
   * Turns diamonds into credits. One way; there is no route back.
   *
   * Answers the whole overview like every other economy mutation, because the
   * conversion moves *both* balances — a client patching one from a narrower
   * response would be showing a credit total it computed itself, against a rate
   * it had a copy of.
   */
  convertDiamonds(diamonds: number) {
    return request<GarageOverview>('/api/economy/convert', {
      method: 'POST',
      body: JSON.stringify({ diamonds }),
    });
  },

  createMission(kind: MissionKind, difficulty?: MissionDifficulty) {
    return request<{ arena: ArenaSummary }>('/api/battle/missions', {
      method: 'POST',
      body: JSON.stringify({ kind, ...(difficulty ? { difficulty } : {}) }),
    });
  },

  /** One-shot credential for the arena socket. See `ArenaTicketService`. */
  arenaTicket() {
    return request<{ ticket: string; expiresInSeconds: number }>('/api/game/arena-ticket', {
      method: 'POST',
    });
  },

  marketListings(query: { kind?: MarketKind; sort?: MarketSort; page?: number } = {}) {
    const params = new URLSearchParams();
    if (query.kind) params.set('kind', query.kind);
    if (query.sort) params.set('sort', query.sort);
    if (query.page) params.set('page', String(query.page));
    const qs = params.toString();
    return request<MarketBrowse>(`/api/market/listings${qs ? `?${qs}` : ''}`);
  },

  marketSellable() {
    return request<MarketSellable[]>('/api/market/sellable');
  },

  marketMine() {
    return request<MarketMine>('/api/market/mine');
  },

  /** Only ids and a price are sent: the server owns what the item is worth. */
  marketList(body: {
    kind: MarketKind;
    itemId: string;
    hullId?: string | null;
    price: number;
    hours: 48 | 96;
  }) {
    const { hullId, ...rest } = body;
    return request<GarageOverview>('/api/market/list', {
      method: 'POST',
      body: JSON.stringify(hullId ? { ...rest, hullId } : rest),
    });
  },

  marketCancel(listingId: string) {
    return request<GarageOverview>('/api/market/cancel', {
      method: 'POST',
      body: JSON.stringify({ listingId }),
    });
  },

  marketBuy(listingId: string) {
    return request<GarageOverview>('/api/market/buy', {
      method: 'POST',
      body: JSON.stringify({ listingId }),
    });
  },

  garage() {
    return request<GarageOverview>('/api/garage');
  },

  spec() {
    return request<TankSpec>('/api/garage/spec');
  },

  purchase(itemId: string) {
    return request<GarageOverview>('/api/garage/purchase', {
      method: 'POST',
      body: JSON.stringify({ itemId }),
    });
  },

  /** Starts installing the next upgrade level (credits now, the level on install). */
  upgrade(itemId: string) {
    return request<GarageOverview>('/api/garage/upgrade', {
      method: 'POST',
      body: JSON.stringify({ itemId }),
    });
  },

  /** Installs a finished upgrade. */
  installUpgrade(itemId: string) {
    return request<GarageOverview>('/api/garage/upgrade/install', {
      method: 'POST',
      body: JSON.stringify({ itemId }),
    });
  },

  /** Finishes a running install for diamonds and installs it; the server prices it. */
  skipUpgrade(itemId: string) {
    return request<GarageOverview>('/api/garage/upgrade/skip', {
      method: 'POST',
      body: JSON.stringify({ itemId }),
    });
  },

  /**
   * Buys a tank bundle: a hull and a turret together, for diamonds.
   *
   * Only the bundle id is sent. The price, the two items, the gates it bypasses
   * and what happens when one half is already owned are all read server-side —
   * a client that posted a price would be posting a number nobody looks at.
   */
  buyBundle(bundleId: string, offerId?: string) {
    return request<GarageOverview>('/api/garage/bundle', {
      method: 'POST',
      // `offerId` pins a limited offer's quote — still an id, never a price.
      // The server refuses if that offer has closed, which is the honest
      // outcome for a card that quoted the sale figure.
      body: JSON.stringify({ bundleId, ...(offerId ? { offerId } : {}) }),
    });
  },

  /** One top-tier upgrade level, paid for in diamonds. */
  prestige(itemId: string) {
    return request<GarageOverview>('/api/garage/prestige', {
      method: 'POST',
      body: JSON.stringify({ itemId }),
    });
  },

  /**
   * Buys a hull part **for one hull**. Owning it and fitting it are separate
   * calls, and owning it is now a fact about the chassis rather than about the
   * account — so the hull is half of what is being paid for, not context.
   */
  buyPart(hullId: string, partId: string) {
    return request<GarageOverview>('/api/garage/parts/buy', {
      method: 'POST',
      body: JSON.stringify({ hullId, partId }),
    });
  },

  /**
   * The eight part slots of one owned hull.
   *
   * Fetched on demand rather than shipped with the overview: parts are per hull,
   * so a garage load that pre-built every owned hull's window would do the work
   * eight times over to answer a question the player has not asked. The equipped
   * hull's window still rides along with the overview, because it is the one the
   * sidebar button opens.
   */
  customise(hullId: string) {
    return request<GarageCustomise>(`/api/garage/customise/${encodeURIComponent(hullId)}`);
  },

  /** Bolts an owned part into its slot on one hull. */
  equipPart(hullId: string, partId: string) {
    return request<GarageOverview>('/api/garage/parts/equip', {
      method: 'POST',
      body: JSON.stringify({ hullId, partId }),
    });
  },

  /** Starts a research: charges the credits and starts the clock. */
  research(nodeId: string) {
    return request<GarageOverview>('/api/garage/research', {
      method: 'POST',
      body: JSON.stringify({ nodeId }),
    });
  },

  /**
   * Pays diamonds to finish a running research now.
   *
   * The price is deliberately not sent. It is a function of the seconds left,
   * which are still passing while the request is in flight, so the only figure
   * that can be right is the one the server computes when it charges.
   */
  skipResearch(nodeId: string) {
    return request<GarageOverview>('/api/garage/research/skip', {
      method: 'POST',
      body: JSON.stringify({ nodeId }),
    });
  },

  /** Just the balances — cheap enough to re-read when a fuel countdown lands. */
  wallet() {
    return request<Wallet>('/api/economy/wallet');
  },

  /**
   * Buys a diamond pack through core payments (`POST /api/payments/deposits`).
   * Only the pack id, the method and the charge currency are sent — the server
   * prices it (`DiamondCatalogue`) and rejects a currency the method cannot
   * charge in. Returns the gateway redirect.
   */
  buyDiamonds(packId: string, method: string, currency: string) {
    return createDeposit({ method, productId: packId, currency });
  },

  buyBoost(boostId: string) {
    return request<GarageOverview>('/api/economy/boost', {
      method: 'POST',
      body: JSON.stringify({ boostId }),
    });
  },

  /**
   * Instant fuel: one unit, or the tank topped off (the default). The price is
   * never sent — the server prices each tier itself.
   */
  refillFuel(units: FuelRefillKey = 'full') {
    return request<GarageOverview>('/api/economy/fuel/refill', {
      method: 'POST',
      body: JSON.stringify({ units }),
    });
  },

  /** Buys the next fuel tank (+5 capacity). The server prices it. */
  buyFuelTank() {
    return request<GarageOverview>('/api/economy/fuel/tank', { method: 'POST' });
  },

  /**
   * Buys a custom amount of diamonds, priced by the shop's own rate.
   *
   * Only the amount is sent — the server re-derives the price from
   * `customPack.rateCentsPer100` at the moment it charges, exactly as a
   * research skip is priced off the seconds left rather than a figure the
   * client computed a moment earlier.
   */
  buyCustomPack(diamonds: number, method: string, currency: string, baseRateCentsPer100 = CUSTOM_BASE_RATE_CENTS_PER_100) {
    // The custom amount travels as EUR cents — the base rate rounded up to the
    // cent, exactly as the server prices it — and the server maps it back to the
    // same diamond count (refusing a charge no count produces). The player's
    // currency is applied by the server.
    const amountCents = Math.ceil((diamonds * baseRateCentsPer100) / 100);
    return createDeposit({ method, amountCents, currency });
  },

  /** Legacy top-up session (in-flight before core payments) — return-page polling only. */
  topupSessionStatus(sessionId: string) {
    return request<TopupSessionStatus>(`/api/topup/session/${encodeURIComponent(sessionId)}/status`);
  },

  /** Core deposit order status (return-page polling). */
  depositStatus(orderId: string) {
    return request<DepositOrder>(`/api/payments/deposits/${encodeURIComponent(orderId)}`);
  },

  /** The player's own core deposit orders, newest first. */
  deposits() {
    return request<DepositOrder[]>('/api/payments/deposits');
  },

  /** Live deposit methods with their currencies and billing requirement. */
  paymentMethods() {
    return request<PaymentMethodsResponse>('/api/payments/methods');
  },

  /** Core billing identity (sent to the gateway, snapshotted on invoices). */
  billingIdentity() {
    return request<BillingAddressView>('/api/payments/billing-address');
  },

  withdrawalLimits() {
    return request<WithdrawalLimits>('/api/payments/withdrawals/limits');
  },

  withdrawals() {
    return request<Withdrawal[]>('/api/payments/withdrawals');
  },

  requestWithdrawal(body: CreateWithdrawalRequest) {
    return request<CreateWithdrawalResponse>('/api/payments/withdrawals', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  saveBillingIdentity(address: Partial<BillingAddressDto>) {
    return request<BillingAddressView>('/api/payments/billing-address', {
      method: 'PUT',
      body: JSON.stringify(address),
    });
  },

  /**
   * Buys experience with diamonds.
   *
   * A **garage** route rather than an economy one, though it is sold on the shop
   * tab beside the packs: granting XP moves a player's rank, and rank sets both
   * the shop's own gates and the arena's `botTierCap`, so it invalidates a
   * resolved spec — which is the line `GarageController` draws for `prestige` and
   * `bundle` too.
   *
   * Only the diamonds are sent. The XP is derived server-side from
   * `xpPurchase.xpPerDiamond` at the moment it charges, so the amount the card
   * quoted cannot be the amount that lands if the rate moved in between.
   */
  buyXp(diamonds: number) {
    return request<GarageOverview>('/api/garage/xp', {
      method: 'POST',
      body: JSON.stringify({ diamonds }),
    });
  },

  /**
   * Claims a finished daily quest.
   *
   * Only the id is sent. What it pays, what target it had to reach and whether it
   * has already been paid are all read server-side — a client that posted a
   * reward would be posting a number nobody looks at, and a second click is
   * refused by the write rather than by the button.
   *
   * The answer carries both halves: the whole overview, because a claim moves the
   * balance, the xp and the rank derived from it; and the reward itself, because
   * the popup should show what the server actually paid rather than what the card
   * happened to be displaying.
   */
  /** Swaps today's unfinished quests for diamonds. No body: the price is the server's. */
  rerollQuests() {
    return request<{ overview: GarageOverview }>('/api/quests/reroll', { method: 'POST' });
  },

  claimQuest(questId: string) {
    return request<{
      reward: { questId: string; questName: string; credits: number; xp: number };
      overview: GarageOverview;
    }>('/api/quests/claim', {
      method: 'POST',
      body: JSON.stringify({ questId }),
    });
  },


  /**
   * `titleId` accepts `NO_TITLE` from `@shared/titles` to wear none — the one
   * slot with an empty state, and the one the server will clear.
   */
  equip(body: { hullId?: string; turretId?: string; paintId?: string; titleId?: string }) {
    return request<GarageOverview>('/api/garage/equip', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // ─── The profile screen ────────────────────────────────────────────────────
  //
  // Half of these are `/api/auth/*`, `/api/me/*`, `/api/invoices` and
  // `/api/cms/*` — the template's own routes, called straight rather than
  // proxied. See the types above for why.

  /** The Account pane, in one read. */
  account() {
    return request<AccountProfile>('/api/account');
  },

  /**
   * The one editable field.
   *
   * The empty string clears it, which is why this takes a `string` and not a
   * `string | null`: a cleared form field is `''`, and asking every caller to
   * convert that to `null` is asking one of them to forget.
   */
  updateAccount(fullName: string) {
    return request<AccountProfile>('/api/account', {
      method: 'PATCH',
      body: JSON.stringify({ fullName }),
    });
  },

  /**
   * Changes the call sign.
   *
   * Its own route rather than a field on `updateAccount`, because the server can
   * refuse this one — taken, reserved, badly shaped — and a request that carries
   * two independent edits has no way to report that half of it failed. The
   * refusal arrives as an `ApiError` with the server's sentence in it, which is
   * what the pane shows.
   */
  updateCallSign(callSign: string) {
    return request<AccountProfile>('/api/account/callsign', {
      method: 'PATCH',
      body: JSON.stringify({ callSign }),
    });
  },

  /**
   * Saves the billing address.
   *
   * Sends the whole address rather than the changed fields: it is one thing a
   * player edits as a block, and the server appends a version of it. Empty
   * strings are fine and mean "cleared" — the server folds them to null.
   */
  updateBillingAddress(address: BillingAddress) {
    return request<AccountProfile>('/api/account/billing-address', {
      method: 'PATCH',
      body: JSON.stringify({
        line1: address.line1 ?? '',
        line2: address.line2 ?? '',
        city: address.city ?? '',
        postcode: address.postcode ?? '',
        country: address.country ?? '',
      }),
    });
  },

  /**
   * Changes the password and ends every session, this one included.
   *
   * The server clears both cookies, so the correct thing for the caller to do
   * afterwards is sign the player out locally — see the Account pane. The
   * confirmation field is not sent: it exists to catch a typo in a box nobody can
   * read, which is the form's business and not the server's.
   */
  changePassword(currentPassword: string, newPassword: string) {
    return request<void>('/api/account/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  /**
   * Asks for another verification email.
   *
   * `devVerifyUrl` is present only off production, where a stack with no SMTP
   * would otherwise make the address unconfirmable and lock the player out of a
   * game that needs it — see `AccountService.resendVerification`. The token in it
   * is a real one and is still redeemed through `verifyEmail` below.
   */
  resendVerification() {
    return request<{ sent: boolean; devVerifyUrl?: string }>(
      '/api/account/resend-verification',
      { method: 'POST' },
    );
  },

  /** Redeems a verification token. The template's endpoint, untouched. */
  verifyEmail(token: string) {
    return request<{ verified: boolean }>('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  /**
   * Sends the welcome email, once, after a successful verification.
   *
   * Called by the verify screen rather than fired by the server because nothing
   * on the server *is* the moment of verification — core's endpoint is the
   * template's and publishes no event. Idempotent, so calling it from two tabs
   * sends one email.
   */
  welcome() {
    return request<{ welcomed: boolean }>('/api/account/welcome', { method: 'POST' });
  },

  /** Starts a password reset from the signed-out screen. The template's. */
  forgotPassword(email: string, altcha: string) {
    return request<{ queued: boolean }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, altcha }),
    });
  },

  /** Finishes one. The template's. */
  resetPassword(token: string, newPassword: string, altcha: string) {
    return request<{ reset: boolean }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword, altcha }),
    });
  },

  /** Language and currency. The template's route — see `AccountController`. */
  preferences() {
    return request<AccountPreferences>('/api/me/preferences');
  },

  /**
   * Writes one or both. The server validates each against the enabled lists, so
   * a stale dropdown is refused rather than stored.
   */
  updatePreferences(body: { locale?: string; currency?: string }) {
    return request<AccountPreferences>('/api/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /** The options for those two dropdowns. Public, and the only source of them. */
  i18nConfig() {
    return request<I18nConfig>('/api/i18n/config');
  },

  /** The Billing pane. The template's. */
  invoices(page = 1) {
    return request<Paged<InvoiceRow>>(`/api/invoices?page=${page}`);
  },

  /**
   * Where the PDF lives.
   *
   * Returned as a URL rather than fetched, because the browser downloading it
   * directly is what makes `Content-Disposition: attachment` work — fetching it
   * into a blob would mean building a download the viewer's own sandbox rules
   * then have to allow, for no gain.
   */
  invoicePdfUrl(number: string) {
    return `${BASE}/api/invoices/${encodeURIComponent(number)}/download`;
  },

  /** The Purchases pane. */
  purchases(page = 1, kind: 'all' | 'diamonds' | 'credits' = 'all') {
    return request<Paged<PurchaseRow>>(`/api/account/purchases?page=${page}&kind=${kind}`);
  },

  /**
   * Deletes the account, irreversibly.
   *
   * A body on a `DELETE` because both fields are credentials, and credentials do
   * not belong in a query string where they land in logs and history.
   */
  deleteAccount(password: string, confirmEmail: string) {
    return request<{ deleted: true; invoicesArchived: number }>('/api/account', {
      method: 'DELETE',
      body: JSON.stringify({ password, confirmEmail }),
    });
  },

  /**
   * One of the three legal documents. The template's CMS.
   *
   * `LandingScreen` already reads these through `landing/site-api.ts`, which uses
   * plain `fetch` because a visitor there provably has no session for the 401
   * retry to help. Inside the app the session exists, so the ordinary `request`
   * is right — and both readers hit the same endpoint, so the documents cannot
   * differ between the landing page and the profile screen.
   */
  legalDocument(slug: string) {
    return request<LegalDocument>(`/api/cms/legal/${encodeURIComponent(slug)}`);
  },

  /**
   * The lobby room as it stands.
   *
   * The chat socket sends this on connect, so a client with a working socket
   * never calls it. It is the fallback for a browser or a proxy that will not
   * hold a WebSocket — see `chat/chat-store.ts`.
   */
  chatHistory() {
    return request<{ messages: ChatMessage[] }>('/api/chat/history');
  },

  /**
   * Says something in the lobby.
   *
   * Sending is HTTP while receiving is a socket, and that is deliberate on the
   * server's side too: every guard, the throttle and the payload validation a
   * send needs already exist on the HTTP path, and chat is a few messages a
   * minute rather than a hot path. See `game/chat/chat.controller.ts`.
   */
  chatSay(body: string) {
    return request<ChatMessage>('/api/chat/say', {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  },
};
